import type { ganttCopy } from '../pt/gantt';

/**
 * English — cronograma (Gantt), grade de tarefas e colunas.
 *
 * Vocabulário de gestão de projetos do inglês, alinhado com o que MS Project e
 * Smartsheet mostram: Indent/Outdent, Predecessors, Critical Path, Baseline.
 * "Baseline", "Backlog" e "Sprint" ficam iguais nos três idiomas porque já
 * nascem em inglês.
 *
 * A máscara de data continua dia/mês/ano: `parseDateInput` só aceita esse
 * formato, então um exemplo mm/dd/yyyy ensinaria o usuário a digitar o que a
 * validação recusa. Só as letras mudam (aaaa -> yyyy).
 */
const gantt: ganttCopy = {
  module: {
    filterAssigned: 'Assignee',
    filterTitle: 'Filter by assignee',
    clear: 'Clear',
    clearFilters: 'Clear filters',
    filterStats: '{{shown}} of {{total}} tasks',

    baseline: 'Baseline',
    baselineSave: 'Save Baseline',
    baselineSaved: 'Baseline saved!',
    baselineShow: 'Show Baseline',
    baselineHide: 'Hide Baseline',
    baselineClear: 'Clear Baseline',
    baselineCleared: 'Baseline removed.',
    baselineClearConfirm: 'Remove the baseline? The original plan kept for comparison will be lost.',

    criticalPath: 'Critical Path',
    criticalPathActive: 'Critical Path on',
    criticalPathOff: 'Turn off Critical Path',
    cpCritical: { zero: 'critical', one: 'critical', other: 'critical' },
    cpNearCritical: { zero: 'near-critical', one: 'near-critical', other: 'near-critical' },
    cpTask: { zero: 'tasks', one: 'task', other: 'tasks' },
    cpOfTotal: 'of {{total}} {{tasks}}',
    cpLegendCritical: 'critical',
    cpLegendNear: 'near-critical',

    resizeHint: 'Drag to resize',
    addTask: 'Add task',

    empty: {
      title: 'Your schedule is empty',
      description: 'Add your first task to start planning the project. You can also paste tasks from a spreadsheet with Ctrl+V.',
      action: 'Add First Task',
      readonlyTitle: 'This project has no tasks yet',
      readonlyDescription: 'Wait until the project owner adds tasks to the schedule.',
    },
  },

  /**
   * O desenho do gráfico. "Float" é o termo do PMBoK em inglês para folga
   * (também aparece como "slack"), e "near-critical" é o mesmo adjetivo que o
   * chip do caminho crítico já usa.
   */
  chart: {
    today: 'Today',
    weekShort: 'Wk {{number}}',
    criticalTask: 'Critical task',
    nearCritical: 'Near-critical',
    epic: 'Epic (computed dates)',
    criticalWarning: 'Delaying this task pushes out the project finish date.',
    float: 'Float: {{days}} {{unit}}',
    dayUnit: { zero: 'days', one: 'day', other: 'days' },
    baselineOnTime: 'Baseline: on time',
    baselineVariance: 'Baseline: {{delta}} days',
    ariaCritical: '{{task}}, critical task, 0 days of float',
    ariaNearCritical: '{{task}}, near-critical, {{days}} days of float',
  },

  table: {
    selected: { one: '1 task selected', other: '{{count}} tasks selected' },
    indent: 'Indent',
    outdent: 'Outdent',
    link: 'Link',
    linkTooltip: 'Link selected tasks as predecessors',
    deleteSelected: 'Delete {{count}}',
    clear: 'Clear',
    selectionHint: 'Tab/Shift+Tab to indent | Esc to clear',

    tableOptions: 'Table options',
    addColumn: 'Add column',
    manageColumns: 'Manage columns',
    manageColumnsTitle: 'Manage visible columns',

    addTask: 'Add task',
    pasteHint: 'Paste tasks (Ctrl+V)',
    pasteAdded: { one: '{{count}} task added', other: '{{count}} tasks added' },

    confirmDeleteTitle: 'Delete task',
    confirmDeleteMessage: 'Delete the selected task? This action cannot be undone.',
    confirmDeleteLabel: 'Delete',

    openDetails: 'Open task details',
  },

  header: {
    edit: 'Edit column',
    hide: 'Hide column',
    show: 'Show column',
    delete: 'Delete column',
  },

  config: {
    editTitle: 'Edit column',
    newTitle: 'New column',
    name: 'Name',
    namePlaceholder: 'Enter a column name',
    type: 'Column type',
    systemColumn: 'This is a system column. Its type cannot be changed.',
    currentType: 'Current type:',
    listOptions: 'List options',
    optionPlaceholder: 'Add option',
    addOption: 'Add',
    symbolSet: 'Symbol set',
    symbolSetPlaceholder: 'Select the set',
    symbolSets: {
      priority: 'Priority',
      status: 'Status',
      flags: 'Flags',
    },
    readonlyNote: 'This column is read-only',
    cancel: 'Cancel',
    save: 'Save',
    create: 'Ok',
    typeChangeTitle: 'Change column type',
    typeChangeMessage: 'Changing the column type may lose existing data or display it incorrectly. For example, if the column holds text and you switch to "Date", text values will not be converted. Continue?',
    typeChangeConfirm: 'Continue',
  },

  visibility: {
    title: 'Visible columns',
    system: 'System',
    lastVisible: 'At least one column must stay visible',
    resetWarning: 'This restores every column to the original default. Custom columns will be deleted.',
    cancel: 'Cancel',
    confirm: 'Confirm',
    reset: 'Restore default',
  },

  comment: {
    title: 'Note',
    placeholder: 'Type your note here...',
    saveHintPrefix: 'Press',
    saveHintSuffix: 'to save',
    cancel: 'Cancel',
    save: 'Save',
  },

  cell: {
    taskNamePlaceholder: 'Task name',
    calculated: 'Calculated automatically from subtasks',
    computedValue: 'Calculated value',
    readonly: 'Read-only',
    hoursPlaceholder: '0h',
    costPending: 'Calculated cost — available in PR 6',
    epicNoPredecessors: 'Epics cannot have predecessors. Dates are calculated automatically from the subtasks.',
    epicNoPredecessorsShort: 'Epics cannot have predecessors - dates are calculated from subtasks',
    autoBadge: 'Auto',
    autoTooltip: 'Auto-calculated',
    backlog: 'Backlog',
    selectPlaceholder: 'Select',
    addComment: 'Click to add a note',
    addPlaceholder: 'Add...',
    addDescription: 'Click to add a description',
    descriptionPlaceholder: 'Describe the task...',

    selectEmpty: 'Select...',
    datePlaceholder: 'dd/mm/yyyy',
    editHint: 'Double-click to edit',

    /** Conversation column (task_comments, the real feed with author and notifications). */
    conversationEmpty: 'No comments yet. Click to start the conversation.',
    conversationLoading: 'Loading comments...',
    conversationHint: 'Click to open the conversation',
  },

  contextMenu: {
    openDetails: 'Open details',
    formatting: 'Text formatting',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    addChild: 'Add subtask',
    addAbove: 'Add task above',
    addBelow: 'Add task below',
    duplicate: 'Duplicate',
    moveUp: 'Move up',
    moveDown: 'Move down',
    indent: 'Indent',
    outdent: 'Outdent',
    dependencies: 'Manage dependencies',
    dependenciesLocked: 'Available on the Starter plan',
    highlight: 'Highlight row',
    highlightSelected: 'Highlight {{count}} selected',
    deleteSelected: 'Delete {{count}} selected',
    delete: 'Delete',
  },

  dependencies: {
    title: 'Manage Dependencies',
    taskLabel: 'Task:',
    current: 'Current dependencies',
    emptyCurrent: 'No dependency selected',
    lagTooltip: 'Lag: +2d = 2 days after, -1d = 1 day before',
    lagHint: 'Lag: +2d = starts 2 days after, -1d = starts 1 day before the predecessor finishes',
    available: 'Available tasks',
    availableHint: 'Select the tasks that must finish before this one',
    emptyAvailable: 'No task available',
    cancel: 'Cancel',
    save: 'Save',
  },

  columns: {
    rowNumber: '#',
    name: 'Task name',
    conversation: 'Conversation',
    description: 'Description',
    predecessors: 'Predecessors',
    duration: 'Duration',
    startDate: 'Start date',
    endDate: 'End date',
    assigned: 'Assigned to',
    sprint_id: 'Sprint',
    priority: 'Priority',
    progress: 'Progress',
    status: 'Status',
    estimated_hours: 'Estimated (h)',
    actual_hours: 'Actual (h)',
    remaining_hours: 'Remaining (h)',
    baseline_estimated_hours: 'Original Estimate (h)',
    hours_variance: 'Variance (h)',
    planned_cost: 'Planned Cost',
    actual_cost: 'Actual Cost',
  },

  columnTypes: {
    text: { name: 'Text/number', description: 'Free text or number field' },
    select: { name: 'Dropdown list', description: 'List of predefined options' },
    date: { name: 'Date', description: 'Date picker' },
    duration: { name: 'Duration', description: 'Duration in days, weeks, etc.' },
    hours: { name: 'Hours', description: 'Value in hours (estimated, actual, remaining, baseline)' },
    money: { name: 'Currency (R$)', description: 'Monetary value derived from hours × rate' },
    people: { name: 'Contact list', description: 'Members of the project team' },
    checkbox: { name: 'Checkbox', description: 'Checked or unchecked' },
    symbols: { name: 'Symbols', description: 'Priority or status symbols' },
    autonumber: { name: 'Auto-number', description: 'Automatic sequential number' },
    // "Note", not "Comment": real comments are the task_comments feed
    // (Conversation column); this type is free text with no author or history.
    comment: { name: 'Note', description: 'Free note field (no history or notifications)' },
    progress: { name: 'Progress', description: 'Progress bar 0-100%' },
    status: { name: 'Status', description: 'Task status (auto-calculated or manual)' },
    sprint: { name: 'Sprint', description: 'Sprint of the project' },
    description: { name: 'Description', description: 'Detailed task description (rich text)' },
  },

  status: {
    not_started: 'Not started',
    in_progress: 'In progress',
    completed: 'Completed',
    paused: 'Paused',
    blocked: 'Blocked',
  },

  priority: {
    none: '—',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  },

  symbols: {
    priority: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      none: 'None',
    },
    status: {
      not_started: 'Not started',
      in_progress: 'In progress',
      blocked: 'Blocked',
      completed: 'Completed',
    },
    flags: {
      flag_red: 'Red flag',
      flag_yellow: 'Yellow flag',
      flag_green: 'Green flag',
      none: 'No flag',
    },
  },

  highlightColors: {
    none: 'No color',
    yellow: 'Yellow',
    pink: 'Pink',
    blue: 'Blue',
    green: 'Green',
    purple: 'Purple',
    orange: 'Orange',
    gray: 'Gray',
  },
};

export default gantt;
